import { IMG, QUALITY } from "../data/content";
import { PageHero, Chapter, Reveal, Btn, useTitle } from "../components/shared";

export default function About() {
  useTitle("About Us | The Big Wedd — Luxury Wedding & Event Planners");
  return (
    <main>
      <PageHero
        eyebrow="About Us"
        title={[<span key="t">Our <span className="italic text-gold-light">story</span></span>]}
        sub="A team of professionals passionate about designing timeless, luxurious weddings and memorable celebrations."
        img={IMG.storyA}
      />

      <section className="bg-ivory py-24 md:py-32" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <Chapter num="01" title="Our Story" />
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] text-emerald">
                  Celebration is an <span className="italic text-gold-text">art</span>
                </h2>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-emerald">
                At The Big Wedd, we are a team of professionals passionate about designing timeless, luxurious weddings and memorable celebrations.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-stone2">
                As destination wedding planners working across Rajasthan, Goa, Rishikesh and destinations across India and abroad, we bring together creativity, planning, design, hospitality and flawless execution under one roof.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-stone2">
                From the first idea to the final farewell, we manage every detail of your celebration so you can be present in the moment.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 grid grid-cols-2 gap-4">
                <img src={IMG.storyB} alt="Reception detail" loading="lazy" className="h-56 md:h-72 w-full object-cover" />
                <img src={IMG.storyC} alt="Celebration detail" loading="lazy" className="mt-8 h-56 md:h-72 w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-sand py-24 md:py-32" data-testid="founder-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative mx-auto w-72 md:w-96">
                <div className="gold-frame arch overflow-hidden">
                  <img src={IMG.founder} alt="Isha Sidhu — Founder, The Big Wedd" loading="lazy" className="h-[420px] md:h-[540px] w-full object-cover" />
                </div>
                <div className="pointer-events-none absolute -left-6 bottom-10 h-20 w-20 rotate-45 border border-gold/50" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Chapter num="02" title="The Founder" />
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl md:text-7xl text-emerald leading-none">Isha Sidhu</h2>
              <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-gold-text">The Driving Force Behind The Big Wedd</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-stone2">
                With over five years of expertise in event and wedding planning, Isha Sidhu is the driving force behind The Big Wedd.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-stone2">
                Known for her innovative approach to destination wedding planning, Isha brings together creativity, meticulous planning and seamless execution to create celebrations that are personal, memorable and beautifully designed.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-stone2">
                Her experience across diverse destinations, wedding formats and celebrations has shaped The Big Wedd's approach to creating thoughtfully curated experiences — from the first concept and venue selection to decor, design, entertainment, hospitality and final execution.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="mt-8 border-l-2 border-gold pl-6 font-serif text-2xl md:text-3xl italic leading-snug text-emerald">
                “For Isha, every wedding is more than an event. It is a story, and every detail should reflect the people at the heart of it.”
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32" data-testid="quality-policy">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="text-center">
            <Chapter num="03" title="Our Quality Policy" center />
            <Reveal delay={0.1}>
              <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl md:text-6xl leading-[1.05] text-emerald">
                Quality in every detail. <span className="italic text-gold-text">Commitment in every celebration.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mx-auto mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-stone2">
                At The Big Wedd, we believe that a memorable celebration is built on quality, attention to detail and consistent execution. From the first conversation to the final farewell, we are committed to maintaining high standards across every aspect of your celebration.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {QUALITY.map((q, i) => (
              <Reveal key={q.title} delay={(i % 3) * 0.1}>
                <div className="group h-full border border-gold/25 bg-white/70 p-8 transition-all duration-500 hover:border-gold/60 hover:shadow-[0_20px_50px_rgba(197,160,89,0.15)]" data-testid={`quality-principle-${i}`}>
                  <span className="font-serif text-3xl italic text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-2xl text-emerald">{q.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone2">{q.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="flex h-full flex-col items-start justify-end bg-emerald p-8" data-testid="quality-closing">
                <p className="font-serif text-2xl md:text-3xl italic leading-snug text-gold-light">
                  “Because your celebration deserves nothing less than your vision, beautifully executed.”
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="mt-16 text-center">
            <Btn to="/contact" testid="about-cta">Plan My Wedding</Btn>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
